import axios from '@/lib/axios'
import {
  buildAdminRequestConfig,
  type OrganizationId,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import { getDeliveryShareFilters, type DeliveryShareFilters } from './delivery-shares.schema'
import type {
  DeliveryRouteLocation,
  DeliveryShare,
  DeliveryShareIdInput,
  DeliveryShareInput,
  DeliveryShareLiveState,
  DeliveryShareMessage,
  SendDeliveryShareInput,
  UpdateDeliveryShareInput,
} from './delivery-shares.types'

export interface DeliveryShareOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for reading or changing one route; the Laravel routes require its id. */
export interface DeliveryShareScope extends DeliveryShareOrganizationScope {
  deliveryShareId: DeliveryShareIdInput
}

/** The show and live-state query keys' entries; the shared key builder can't mark an entry required. */
export interface DeliveryShareShowKeyEntries extends DeliveryShareOrganizationScope {
  deliveryShareId?: DeliveryShareIdInput
}

export type DeliveryShareIndexQueryKeyEntries = DeliveryShareOrganizationScope & {
  filters?: DeliveryShareFilters | undefined
}

/** Query functions read only the key. */
type DeliveryShareQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

export async function getDeliveryShares({
  queryKey: [{ organizationId, filters = {} }],
}: DeliveryShareQueryContext<DeliveryShareIndexQueryKeyEntries>) {
  const parsedFilters = getDeliveryShareFilters(filters)

  return (
    (await axios.get<DeliveryShare[]>(
      '/delivery-shares',
      buildAdminRequestConfig({
        organizationId,
        params: {
          ...(parsedFilters.salesOrderId ? { sales_order_id: parsedFilters.salesOrderId } : {}),
        },
      })
    )) || []
  )
}

export async function getDeliveryRouteLocations({
  queryKey: [{ organizationId }],
}: Pick<QueryFunctionContext<ScopedAdminQueryKey>, 'queryKey'>) {
  return (
    (await axios.get<DeliveryRouteLocation[]>(
      '/delivery-route-locations',
      buildAdminRequestConfig({ organizationId })
    )) || []
  )
}

export async function createDeliveryShare({
  organizationId,
  ...values
}: DeliveryShareOrganizationScope & DeliveryShareInput) {
  return axios.post<DeliveryShare>(
    '/delivery-shares',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getDeliveryShare({
  queryKey: [{ organizationId, deliveryShareId }],
}: DeliveryShareQueryContext<DeliveryShareShowKeyEntries>) {
  return axios.get<DeliveryShare>(
    `/delivery-shares/${deliveryShareId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getDeliveryShareLiveState({
  queryKey: [{ organizationId, deliveryShareId }],
}: DeliveryShareQueryContext<DeliveryShareShowKeyEntries>) {
  return axios.get<DeliveryShareLiveState>(
    `/delivery-shares/${deliveryShareId}/live-state`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateDeliveryShare({
  organizationId,
  deliveryShareId,
  ...values
}: DeliveryShareOrganizationScope & UpdateDeliveryShareInput) {
  return axios.patch<DeliveryShare>(
    `/delivery-shares/${deliveryShareId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function sendDeliveryShare({
  organizationId,
  deliveryShareId,
  ...values
}: DeliveryShareScope & SendDeliveryShareInput) {
  return axios.post<DeliveryShareMessage>(
    `/delivery-shares/${deliveryShareId}/share`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function revokeDeliveryShare({ organizationId, deliveryShareId }: DeliveryShareScope) {
  return axios.delete<DeliveryShareMessage>(
    `/delivery-shares/${deliveryShareId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
